/**
 * NZSC-3330 리포트 적정가 모듈 — 순수 판정/계산 헬퍼.
 *
 * 역할
 *  - api-public 응답(데이터 계약 v0)을 화면이 그대로 쓰는 뷰모델로 정규화
 *  - 피그마 케이스 분기(매매 2 / 전세 3 / 반전세·월세 2축) 판정
 *
 * 금액 판정(zone)과 적정 보증금·월세(fair*)는 **서버가 정본**이다. 이 파일은 다시 계산하지 않는다.
 *
 * 이 파일에는 API 호출이 없다. 호출·캐시는 store/modules/report-valuation.js 한 파일로 격리한다.
 * (BE 계약이 흔들려도 이 파일 + 스토어 한 벌만 고치면 되도록)
 */
import { formatAmountUnit } from "@/utils/priceFormat";

export const VALUATION_ZONE = {
  ABOVE: "ABOVE_RANGE",
  IN: "IN_RANGE",
  BELOW: "BELOW_RANGE",
};

export const VALUATION_DEAL_TYPE = {
  SALE: "SALE",
  JEONSE: "JEONSE",
  MONTHLY_RENT: "MONTHLY_RENT",
};

export const VALUATION_SOURCE = {
  DIRECT: "DIRECT_COMPARABLES",
  KB: "KB_DERIVED_PRICE",
  JOOTEK: "JOOTEK_STRUCTURAL",
  JEONSE_RATIO: "SALE_TO_JEONSE_RATIO",
  MONTHLY_CONVERSION: "JEONSE_TO_MONTHLY_CONVERSION",
};

const VALUATION_SOURCE_LABEL = {
  [VALUATION_SOURCE.DIRECT]: "실거래 비교",
  [VALUATION_SOURCE.KB]: "KB 시세 역산",
  [VALUATION_SOURCE.JOOTEK]: "주택 특성 역산",
  [VALUATION_SOURCE.JEONSE_RATIO]: "지역 전세가율 역산",
  [VALUATION_SOURCE.MONTHLY_CONVERSION]: "전월세 전환율 환산",
};

/** 매매 리포트 케이스 — 피그마 3291-46(높음) / 3291-19(낮음) + 동일(EQUAL) */
export const SALE_CASE = {
  ABOVE: "SALE_ABOVE",
  BELOW: "SALE_BELOW",
  EQUAL: "SALE_EQUAL",
};

/** 전세 유저 케이스 — 피그마 833-456(예상 매매가 초과) / 833-320(적정 전세가 초과) / 833-480(낮음) + 동일(EQUAL) */
export const JEONSE_CASE = {
  ABOVE_SALE: "JEONSE_ABOVE_SALE",
  ABOVE: "JEONSE_ABOVE",
  BELOW: "JEONSE_BELOW",
  EQUAL: "JEONSE_EQUAL",
};

/** 반전세·월세 유저 케이스 — 피그마 833-344/833-372(보증금 축), 833-428(월세 축) + 동일(EQUAL) */
export const RENT_CASE = {
  ABOVE: "RENT_ABOVE",
  BELOW: "RENT_BELOW",
  EQUAL: "RENT_EQUAL",
};

/** 반전세·월세 모듈 토글 축 */
export const RENT_AXIS = {
  DEPOSIT: "DEPOSIT",
  MONTHLY: "MONTHLY",
};

/** 막대 최소 높이(px). 값이 0에 가까워도 막대가 사라지지 않도록. */
const MIN_BAR_PX = 20;

function fnNum(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function fnPositive(value) {
  const n = fnNum(value);
  return n !== null && n > 0 ? n : null;
}

function fnEstimateQuality(estimate, confidenceScore, rangeLabel) {
  if (!estimate) return null;
  const lowWon = fnPositive(estimate.band && estimate.band.lowWon);
  const highWon = fnPositive(estimate.band && estimate.band.highWon);
  const hasBand = lowWon !== null && highWon !== null && lowWon <= highWon;
  const source = Object.values(VALUATION_SOURCE).includes(estimate.source)
    ? estimate.source
    : null;
  const grade = ["A", "B", "C"].includes(estimate.confidenceGrade)
    ? estimate.confidenceGrade
    : null;
  const score = Number.isInteger(confidenceScore) && confidenceScore >= 0 && confidenceScore <= 100
    ? confidenceScore
    : null;
  if (!source && !grade && score === null && !hasBand) return null;
  return {
    source,
    sourceLabel: source ? VALUATION_SOURCE_LABEL[source] : "출처 확인 불가",
    anchorSource: Object.values(VALUATION_SOURCE).includes(estimate.anchorSource)
      ? estimate.anchorSource
      : null,
    anchorSourceLabel: Object.values(VALUATION_SOURCE).includes(estimate.anchorSource)
      ? VALUATION_SOURCE_LABEL[estimate.anchorSource]
      : null,
    confidenceGrade: grade,
    confidenceScore: score,
    rangeLabel,
    rangeLowWon: hasBand ? lowWon : null,
    rangeHighWon: hasBand ? highWon : null,
  };
}

/**
 * 원 단위 금액을 피그마 표기("13억 1,000만원")로 변환.
 * priceFormat.formatAmountUnit 은 만원 단위 문자열을 받으므로 여기서 환산해 넘긴다.
 *
 * 값이 없거나 음수면 **표기 자체를 만들지 않는다(null)**. 예전처럼 "0원"으로 뭉개면
 * 적정값이 존재하지 않는 조건에서 "적정 보증금 0원" 같은 거짓 금액이 화면에 나간다.
 */
function fnAmountText(won) {
  const n = fnNum(won);
  if (n === null || n < 0) return null;
  const man = Math.round(n / 10000);
  if (man <= 0) return "0";
  const s = String(man);
  return formatAmountUnit(s.length, s);
}

/** "13억 1,000만원" — 표/막대 금액 표기. 표기 불가면 null(호출부가 그 자리를 비운다) */
export function fnFormatWon(won) {
  const text = fnAmountText(won);
  return text === null ? null : `${text}원`;
}

/** "13억 1,000만 원" — 본문 문장 안 금액 표기(피그마 "약 OOO만 원"). 표기 불가면 null */
export function fnFormatWonSpaced(won) {
  const text = fnAmountText(won);
  return text === null ? null : `${text} 원`;
}

export function fnQualityConfidenceText(quality) {
  if (!quality) return null;
  const parts = [];
  if (["A", "B", "C"].includes(quality.confidenceGrade)) {
    parts.push(`신뢰도 ${quality.confidenceGrade}`);
  }
  if (
    Number.isInteger(quality.confidenceScore) &&
    quality.confidenceScore >= 0 &&
    quality.confidenceScore <= 100
  ) {
    parts.push(`${quality.confidenceScore}점`);
  }
  return parts.length ? parts.join(" · ") : null;
}

export function fnQualityRangeText(quality) {
  if (!quality) return null;
  const low = fnFormatWon(quality.rangeLowWon);
  const high = fnFormatWon(quality.rangeHighWon);
  return low && high ? `${low} ~ ${high}` : null;
}

/**
 * 서버가 내려준 zone 문자열 검증. 금액 판정은 **서버 단일 정본**이므로
 * 프론트는 값이 규격에 맞는지만 보고, 없으면 그 판정을 미노출(null)한다.
 * (클라이언트가 밴드/점추정으로 zone 을 다시 계산하면 서버 규칙 — 상단 +3% 마진 등 — 과 어긋난다)
 */
export function fnNormalizeZone(zone) {
  if (
    zone === VALUATION_ZONE.ABOVE ||
    zone === VALUATION_ZONE.IN ||
    zone === VALUATION_ZONE.BELOW
  ) {
    return zone;
  }
  return null;
}

/**
 * 두 금액을 막대 높이(px)로 환산. 큰 쪽이 maxPx, 작은 쪽은 비례.
 * (피그마: 매매 최대 110px, 전월세 최대 100px — 833-458 annotation "최대높이 100px")
 */
export function fnBarHeights(aWon, bWon, maxPx) {
  const a = fnNum(aWon) || 0;
  const b = fnNum(bWon) || 0;
  const max = Math.max(a, b);
  if (max <= 0) return { a: maxPx, b: maxPx };
  const scale = v => Math.max(MIN_BAR_PX, Math.min(maxPx, Math.round((v / max) * maxPx)));
  return { a: scale(a), b: scale(b) };
}

/**
 * api-public 응답 → 화면 뷰모델.
 * 게이트 미통과·데이터 없음·산정 보류(ABSTAINED)는 서버가 module:null 로 내려주는 계약이므로
 * 프론트는 module 유무와 "그리는 데 필요한 값이 다 있는지"만 본다. 하나라도 없으면 미노출(null).
 *
 * 금액 판정(zone)과 적정 보증금·월세는 **서버가 정본**이다. 프론트는 재계산하지 않고,
 * 서버 값이 없으면 그 판정/축을 미노출한다.
 *
 * @param {object|null} payload GET /api/v1/report/{reportSeq}/valuation 응답
 * @returns {object|null} 뷰모델 (null = 모듈·메뉴 항목 전부 미노출)
 */
export function fnBuildValuationView(payload) {
  const raw = payload && payload.module;
  if (!raw) return null;

  const myDeal = raw.myDeal || {};
  const dealType = myDeal.dealType;
  const assessment = raw.assessment || {};
  const sale = raw.sale || null;
  const jeonse = raw.jeonse || null;
  const monthly = raw.monthlyRent || null;
  // payload v2는 월세 primary가 jeonse 병기값과 다를 수 있다. 새 계약을 우선하고,
  // 구 API 응답만 거래유형별 기존 슬롯으로 폴백한다.
  const primaryEstimate =
    raw.primaryEstimate || (dealType === VALUATION_DEAL_TYPE.SALE ? sale : jeonse);

  const salePointWon = fnPositive(sale && sale.pointEstimateWon);
  const jeonsePointWon = fnPositive(jeonse && jeonse.pointEstimateWon);
  const annualConversionRate = fnPositive(monthly && monthly.annualConversionRate);

  const base = {
    reportType: raw.reportType || null,
    dealType: dealType || null,
    salePointWon,
    jeonsePointWon,
    annualConversionRate,
    algorithmVersion: raw.algorithmVersion || null,
    dataCutoffDate: raw.dataCutoffDate || null,
  };

  if (dealType === VALUATION_DEAL_TYPE.SALE) {
    const myPriceWon = fnPositive(myDeal.priceWon);
    if (myPriceWon === null || salePointWon === null) return null;
    const zone = fnNormalizeZone(assessment.zone);
    const saleCase = fnResolveSaleCase(zone, myPriceWon, salePointWon);
    if (!saleCase) return null;
    return {
      ...base,
      quality: fnEstimateQuality(primaryEstimate, raw.confidenceScore, "예상 매매가 범위"),
      myPriceWon,
      zone,
      saleCase,
      diffWon: Math.abs(myPriceWon - salePointWon),
    };
  }

  if (dealType === VALUATION_DEAL_TYPE.JEONSE) {
    const myDepositWon = fnPositive(myDeal.depositWon);
    // 병기값인 예상 매매가(sale)는 그쪽만 산정보류일 수 있다 → 없으면 해당 행만 빠지고 모듈은 나간다
    if (myDepositWon === null || jeonsePointWon === null) return null;
    const zone = fnNormalizeZone(assessment.zone);
    const jeonseCase = fnResolveJeonseCase(zone, myDepositWon, salePointWon, jeonsePointWon);
    if (!jeonseCase) return null;
    return {
      ...base,
      quality: fnEstimateQuality(primaryEstimate, raw.confidenceScore, "예상 전세가 범위"),
      myDepositWon,
      zone,
      jeonseCase,
      // 예상 매매가 초과 케이스(833-456)는 매매가 기준 차액, 그 외는 적정 전세가 기준 차액
      diffWon:
        jeonseCase === JEONSE_CASE.ABOVE_SALE
          ? Math.abs(myDepositWon - salePointWon)
          : Math.abs(myDepositWon - jeonsePointWon),
    };
  }

  if (dealType === VALUATION_DEAL_TYPE.MONTHLY_RENT) {
    const myDepositWon = fnNum(myDeal.depositWon);
    const myMonthlyRentWon = fnPositive(myDeal.monthlyRentWon);
    if (myDepositWon === null || myMonthlyRentWon === null) return null;

    // 적정 보증금(내 월세 고정) / 적정 월세(내 보증금 고정)는 **서버 계약이 정본**이다.
    // 서버는 값이 음수면(그 조건에서 적정값이 존재하지 않음) null 로 내려준다 — 0 clamp 하지 않는다.
    // 프론트가 다시 계산하면 서버 규칙과 어긋나고, 0 clamp 하면 "적정 보증금 0원"이 화면에 나간다.
    // 서버 값이 없는 축은 축 자체를 미노출, 두 축 다 없으면 모듈 미노출.
    // (pointMonthlyRentWon 은 콜드가 0 clamp 한 값이라 축 표시에 쓰지 않는다)
    const fairDepositWon = fnPositive(monthly && monthly.fairDepositWon);
    const fairMonthlyRentWon = fnPositive(monthly && monthly.fairMonthlyRentWon);
    if (fairDepositWon === null && fairMonthlyRentWon === null) return null;

    const depositZone = fairDepositWon === null ? null : fnNormalizeZone(assessment.depositZone);
    const monthlyZone =
      fairMonthlyRentWon === null ? null : fnNormalizeZone(assessment.monthlyRentZone);

    const depositCase = fnResolveRentCase(depositZone, myDepositWon, fairDepositWon);
    const monthlyCase = fnResolveRentCase(monthlyZone, myMonthlyRentWon, fairMonthlyRentWon);
    // 두 축 다 적정값이 없어 그릴 게 없을 때만 미노출. 적정(동일)은 EQUAL 케이스로 나간다.
    if (!depositCase && !monthlyCase) return null;

    return {
      ...base,
      // 월세 결과의 point/band는 보증금+월세를 합친 등가보증금 기준이다.
      quality: fnEstimateQuality(
        primaryEstimate,
        raw.confidenceScore,
        "보증금·월세 환산 범위",
      ),
      myDepositWon,
      myMonthlyRentWon,
      fairDepositWon,
      fairMonthlyRentWon,
      depositZone,
      monthlyZone,
      depositCase,
      monthlyCase,
      depositDiffWon: fairDepositWon === null ? null : Math.abs(myDepositWon - fairDepositWon),
      monthlyDiffWon:
        fairMonthlyRentWon === null ? null : Math.abs(myMonthlyRentWon - fairMonthlyRentWon),
    };
  }

  return null;
}

/**
 * NZSC-3452: 화면 케이스는 **내 가격과 적정가의 대소**로만 가른다.
 *
 * 종전엔 알고리즘 zone(ABOVE/IN/BELOW)을 그대로 화면 케이스로 썼다. 그래서 적정(IN_RANGE)이면
 * 그릴 프레임이 없다는 이유로 모듈을 통째로 감췄고, 실측상 게이트를 통과한 38건 중 23건이
 * 그렇게 사라졌다(실제 노출 4건). 적정 범위 안에서도 "적정가보다 2천만원 싸게 계약"과
 * "1,400만원 비싸게 계약"은 전혀 다른 정보인데 둘 다 안 보였다.
 *
 * zone 은 알고리즘의 판정으로 그대로 두고(뷰모델에 계속 실린다), **표시**만 대소 비교로 바꾼다.
 * zone=ABOVE 면 내 가격 > 상단 > 점추정이라 대소 비교와 어긋나지 않는다(BELOW 도 대칭).
 */
function fnCompareCase(myWon, fairWon, cases) {
  const mine = fnNum(myWon);
  const fair = fnNum(fairWon);
  if (mine === null || fair === null) return null;
  if (mine > fair) return cases.above;
  if (mine < fair) return cases.below;
  return cases.equal;
}

/** 매매 케이스 판정 — 내 매매가 vs 예상 매매가. */
export function fnResolveSaleCase(zone, myPriceWon, salePointWon) {
  return fnCompareCase(myPriceWon, salePointWon, {
    above: SALE_CASE.ABOVE,
    below: SALE_CASE.BELOW,
    equal: SALE_CASE.EQUAL,
  });
}

/**
 * 전세 케이스 판정 — 내 보증금 vs 적정 전세가.
 * 내 보증금이 예상 매매가 이상이면 '깡통' 케이스(833-456)가 대소 비교보다 우선한다.
 */
export function fnResolveJeonseCase(zone, myDepositWon, salePointWon, jeonsePointWon) {
  const deposit = fnNum(myDepositWon);
  const salePoint = fnNum(salePointWon);
  if (deposit !== null && salePoint !== null && deposit >= salePoint) {
    return JEONSE_CASE.ABOVE_SALE;
  }
  return fnCompareCase(deposit, jeonsePointWon, {
    above: JEONSE_CASE.ABOVE,
    below: JEONSE_CASE.BELOW,
    equal: JEONSE_CASE.EQUAL,
  });
}

/**
 * 전세 차트의 회색(기준) 막대 정의 — 라벨/값/연한색 여부.
 *
 * 예상 매매가 초과 케이스(피그마 833-456)는 비교 기준이 적정 전세가가 아니라 **예상 매매가**다.
 * 라벨('예상 시세')·제목·부제 차액이 전부 예상 매매가 기준이므로 막대 값도 같이 맞춰야 한다.
 * 적정 전세가를 그리면 막대 차이가 부제의 "약 OOO만 원 높아요" 와 어긋난다.
 */
export function fnJeonseChartNeutral(view) {
  const v = view || {};
  if (v.jeonseCase === JEONSE_CASE.ABOVE_SALE) {
    return {
      label: "예상 시세",
      won: v.salePointWon === undefined ? null : v.salePointWon,
      light: true,
    };
  }
  return {
    label: "적정 전세가",
    won: v.jeonsePointWon === undefined ? null : v.jeonsePointWon,
    light: false,
  };
}

/**
 * 전세 문구 분기 — 컴포넌트가 그대로 switch 하는 순수 판정.
 *
 * 케이스와 문구가 1:1이 아니라서 뺀다. 깡통(ABOVE_SALE)은 `보증금 >= 예상 매매가`라
 * **같을 때도** 걸리는데, 그때 차액이 0이라 "약 0 원 높아요"가 나가던 결함이 있었다.
 * 금액이 0인 문장은 케이스마다 따로 막지 말고 여기 한 곳에서 갈라낸다.
 */
export const JEONSE_COPY = {
  ABOVE_SALE: "ABOVE_SALE",
  ABOVE_SALE_EQUAL: "ABOVE_SALE_EQUAL",
  ABOVE: "ABOVE",
  BELOW: "BELOW",
  EQUAL: "EQUAL",
};

export function fnJeonseCopyCase(view) {
  const v = view || {};
  if (v.jeonseCase === JEONSE_CASE.ABOVE_SALE) {
    return v.diffWon === 0 ? JEONSE_COPY.ABOVE_SALE_EQUAL : JEONSE_COPY.ABOVE_SALE;
  }
  if (v.jeonseCase === JEONSE_CASE.EQUAL) return JEONSE_COPY.EQUAL;
  if (v.jeonseCase === JEONSE_CASE.ABOVE) return JEONSE_COPY.ABOVE;
  if (v.jeonseCase === JEONSE_CASE.BELOW) return JEONSE_COPY.BELOW;
  return null;
}

/**
 * 반전세·월세 축별 케이스 판정 — 내 값 vs 그 축의 적정값(보증금 축이면 적정 보증금).
 * 전세·매매와 같은 규칙(NZSC-3452). 적정값이 없는 축은 그릴 게 없으므로 그대로 null.
 */
export function fnResolveRentCase(zone, myWon, fairWon) {
  return fnCompareCase(myWon, fairWon, {
    above: RENT_CASE.ABOVE,
    below: RENT_CASE.BELOW,
    equal: RENT_CASE.EQUAL,
  });
}
